import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, TrendingUpIcon, SearchIcon, ShieldCheckIcon, TargetIcon, SettingsIcon, FileTextIcon, MapPinIcon, PenToolIcon, UserIcon, BuildingIcon, BriefcaseIcon, ShoppingBagIcon, HeartPulseIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function SeoVoorWebsitesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'SEO voor websites | Hoger scoren in Google | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'SEO voor websites: hoger scoren in Google met technische SEO, lokale SEO en content strategie. Noord Digital helpt ondernemers in Noord-Holland met duurzame vindbaarheid.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'SEO voor websites | Hoger scoren in Google | Noord',
      'og:description': 'SEO voor websites: hoger scoren in Google met technische SEO, lokale SEO en content strategie.',
      'og:type': 'website',
      'og:url': 'https://noorddigital.nl/seo-voor-websites',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Hoe lang duurt het voordat SEO resultaat oplevert?',
    a: 'SEO is een langetermijnstrategie. De eerste verbeteringen zie je vaak binnen 3 maanden, maar de grootste resultaten komen na 6 tot 12 maanden consistent werken aan je vindbaarheid.'
  }, {
    q: 'Is SEO beter dan Google Ads?',
    a: 'Beide hebben hun plek. Google Ads levert direct verkeer maar stopt zodra je stopt met betalen. SEO bouwt duurzaam organisch verkeer op dat blijft groeien. Wij adviseren vaak een combinatie.'
  }, {
    q: 'Wat kost SEO optimalisatie?',
    a: 'De kosten hangen af van de huidige staat van je website, je concurrentie en je doelen. Wij bieden transparante SEO-pakketten en bespreken vooraf wat realistisch is voor jouw budget.'
  }, {
    q: 'Kan ik zelf aan SEO werken?',
    a: 'Basis SEO kun je zelf doen, maar voor technische optimalisatie, keyword strategie en structurele verbeteringen is specialistische kennis nodig. Wij combineren beide: wij doen het zware werk, jij leert de basics.'
  }, {
    q: 'Werkt SEO ook voor lokale bedrijven?',
    a: 'Juist voor lokale bedrijven is SEO enorm waardevol. Met lokale SEO zorg je dat klanten in jouw regio (Alkmaar, Noord-Holland) jou vinden wanneer ze zoeken naar jouw diensten.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-1685">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-1686">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1687">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-1688" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-1689" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-1690" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1691">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-1692">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-1693">
              Home
            </a>
            <span className="text-navy-500" data-id="element-1694">/</span>
            <span className="text-accent-light font-medium" data-id="element-1695">
              SEO voor websites
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-1696">
            SEO voor websites — hoger scoren in Google
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-1697">
            Meer organisch verkeer, betere vindbaarheid en meer klanten. Wij
            optimaliseren jouw website voor zoekmachines.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-1698">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-1699">
              Plan een SEO-gesprek
              <ArrowRightIcon className="w-5 h-5" data-id="element-1700" />
            </a>
          </div>
        </div>
      </section>

      {/* Waarom SEO belangrijk is */}
      <section className="py-24 bg-surface-muted" data-id="element-1701">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1702">
          <div className="text-center mb-16" data-id="element-1703">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1704">
              Waarom SEO belangrijk is
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1705">
              Zoekmachine optimalisatie is de meest duurzame manier om online te
              groeien.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1706">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1707">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1708">
                <TrendingUpIcon className="w-6 h-6 text-accent" data-id="element-1709" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1710">
                Meer organisch verkeer
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1711">
                Gratis bezoekers via Google die actief zoeken naar jouw diensten
                of producten.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1712">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1713">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-1714" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1715">
                Hogere Google posities
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1716">
                Verschijn bovenaan de zoekresultaten voor relevante zoekwoorden
                in jouw branche.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1717">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1718">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-1719" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1720">
                Meer vertrouwen
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1721">
                Hoge Google posities stralen autoriteit en betrouwbaarheid uit
                naar potentiële klanten.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1722">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1723">
                <TargetIcon className="w-6 h-6 text-accent" data-id="element-1724" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1725">
                Duurzaam resultaat
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1726">
                SEO levert langdurig resultaat op, ook als je stopt met
                investeren in advertenties.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Onze SEO aanpak */}
      <section className="py-24 bg-white" data-id="element-1727">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1728">
          <div className="text-center mb-16" data-id="element-1729">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1730">
              Onze SEO aanpak
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1731">
              Een bewezen strategie voor betere vindbaarheid en meer conversie.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1732">
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1733">
              <div className="flex items-start gap-4" data-id="element-1734">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1735">
                  <SettingsIcon className="w-6 h-6 text-navy-700" data-id="element-1736" />
                </div>
                <div data-id="element-1737">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1738">
                    Technische SEO
                  </h3>
                  <p className="text-slate-600" data-id="element-1739">
                    Snelle laadtijden, mobiel-vriendelijk, schone code, XML
                    sitemaps en gestructureerde data voor een perfecte basis.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1740">
              <div className="flex items-start gap-4" data-id="element-1741">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1742">
                  <FileTextIcon className="w-6 h-6 text-navy-700" data-id="element-1743" />
                </div>
                <div data-id="element-1744">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1745">
                    On-page SEO
                  </h3>
                  <p className="text-slate-600" data-id="element-1746">
                    Keyword research, meta-tags, koppen structuur, interne
                    linking en content optimalisatie voor maximale relevantie.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1747">
              <div className="flex items-start gap-4" data-id="element-1748">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1749">
                  <MapPinIcon className="w-6 h-6 text-navy-700" data-id="element-1750" />
                </div>
                <div data-id="element-1751">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1752">
                    Lokale SEO
                  </h3>
                  <p className="text-slate-600" data-id="element-1753">
                    Google Mijn Bedrijf, lokale zoekwoorden, NAW-consistentie en
                    reviews strategie om lokaal te domineren.
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card hover:border-accent/30 transition-colors" data-id="element-1754">
              <div className="flex items-start gap-4" data-id="element-1755">
                <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1756">
                  <PenToolIcon className="w-6 h-6 text-navy-700" data-id="element-1757" />
                </div>
                <div data-id="element-1758">
                  <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1759">
                    Content strategie
                  </h3>
                  <p className="text-slate-600" data-id="element-1760">
                    Kennisbank artikelen, blogs en landingspagina's die ranken
                    op relevante zoektermen en bezoekers overtuigen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO resultaten */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-1761">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-1762" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1763">
          <div className="text-center mb-16" data-id="element-1764">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-1765">
              SEO resultaten die je mag verwachten
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-1766">
              Meetbare groei voor jouw bedrijf door doelgerichte optimalisatie.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1767">
            <div className="bg-white/[0.06] border border-white/10 p-8 rounded-2xl text-center" data-id="element-1768">
              <div className="text-4xl font-extrabold text-accent mb-4" data-id="element-1769">
                Top 10 posities
              </div>
              <p className="text-navy-200" data-id="element-1770">
                Voor relevante zoekwoorden in jouw regio
              </p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 p-8 rounded-2xl text-center" data-id="element-1771">
              <div className="text-4xl font-extrabold text-accent mb-4" data-id="element-1772">
                +200% organisch verkeer
              </div>
              <p className="text-navy-200" data-id="element-1773">Gemiddelde groei binnen 6 maanden</p>
            </div>
            <div className="bg-white/[0.06] border border-white/10 p-8 rounded-2xl text-center" data-id="element-1774">
              <div className="text-4xl font-extrabold text-accent mb-4" data-id="element-1775">
                Meer leads
              </div>
              <p className="text-navy-200" data-id="element-1776">
                Bezoekers die actief op zoek zijn naar jouw dienst
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Voor wie is SEO geschikt? */}
      <section className="py-24 bg-surface-muted" data-id="element-1777">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1778">
          <div className="text-center mb-16" data-id="element-1779">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1780">
              Voor wie is SEO geschikt?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1781">
              Onze SEO-diensten zijn effectief voor diverse soorten
              ondernemingen.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4" data-id="element-1782">
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1783">
              <MapPinIcon className="w-5 h-5 text-accent" data-id="element-1784" />
              <span className="font-medium text-navy-900" data-id="element-1785">
                Lokale ondernemers
              </span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1786">
              <BuildingIcon className="w-5 h-5 text-accent" data-id="element-1787" />
              <span className="font-medium text-navy-900" data-id="element-1788">MKB bedrijven</span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1789">
              <UserIcon className="w-5 h-5 text-accent" data-id="element-1790" />
              <span className="font-medium text-navy-900" data-id="element-1791">ZZP'ers</span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1792">
              <ShoppingBagIcon className="w-5 h-5 text-accent" data-id="element-1793" />
              <span className="font-medium text-navy-900" data-id="element-1794">Webshops</span>
            </div>
            <div className="bg-white px-6 py-4 rounded-xl shadow-soft flex items-center gap-3" data-id="element-1795">
              <BriefcaseIcon className="w-5 h-5 text-accent" data-id="element-1796" />
              <span className="font-medium text-navy-900" data-id="element-1797">Dienstverleners</span>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-1798">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1799">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-1800">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1801">
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1802">
              WordPress website laten bouwen
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1803">•</span>
            <a href="#/kosten-website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1804">
              Wat kost een website?
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1805">•</span>
            <a href="#/webdesign-alkmaar" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1806">
              Webdesign bureau Alkmaar
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1807">•</span>
            <a href="#/" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1808">
              Terug naar homepage
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-surface-muted" data-id="element-1809">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1810">
          <div className="text-center mb-16" data-id="element-1811">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1812">
              Veelgestelde vragen over SEO
            </h2>
          </div>

          <div className="space-y-4" data-id="element-1813">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl shadow-soft overflow-hidden" data-id="element-1814">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-1815">
                  <span className="font-bold text-navy-900" data-id="element-1816">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1817" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-1818" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-1819">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-1820" />
    </main>;
}